/**
 * AutoMe AI — branded icon composition.
 *
 * Reusable HTML markup for the AI icon used across the app: the original
 * AutoMe logo (steering wheel + chat bubble) with three branded
 * enhancements layered on top:
 *
 *   1. A teal halo behind it that pulses (2.5s ease-in-out)
 *   2. An orange "Ai" badge in the top-right corner that pulses (1.6s)
 *   3. Four orange sparkles arranged around it, staggered fade-in/out
 *
 * The animations and positions are CSS-driven (see styles/screens/home.css
 * `.ai-icon` block). The container must have `overflow: visible` so the
 * halo and sparkles can extend slightly past the icon bounds.
 *
 * @example
 *   import { iconHtml } from './icon.js';
 *   el.innerHTML = iconHtml(60);  // 60px FAB-sized icon
 */

/**
 * Returns the HTML markup for the icon at the requested container size.
 *
 * Currently supported sizes (matching CSS variants):
 *   - 28 — extra-small (inline within text — daily brief chip)
 *   - 40 — small (chat header avatar)
 *   - 60 — medium (FAB)
 *   - 96 — large (welcome hero)
 *
 * Unknown sizes fall back to the closest variant.
 *
 * @param {28 | 40 | 60 | 96} [size=60]
 * @returns {string} HTML
 */
export function iconHtml(size = 60) {
  const validSize = [28, 40, 60, 96].includes(size) ? size : 60;
  return `
    <span class="ai-icon ai-icon-s${validSize}" data-ai-icon>
      <span class="ai-icon-halo" aria-hidden="true"></span>
      <img class="ai-icon-img" src="/icons/icon-512.png" alt="" aria-hidden="true">
      <span class="ai-icon-badge" aria-hidden="true">A<span class="ai-icon-i">i</span></span>
      <span class="ai-icon-sp ai-icon-sp-1" aria-hidden="true">✦</span>
      <span class="ai-icon-sp ai-icon-sp-2" aria-hidden="true">✦</span>
      <span class="ai-icon-sp ai-icon-sp-3" aria-hidden="true">✦</span>
      <span class="ai-icon-sp ai-icon-sp-4" aria-hidden="true">✦</span>
    </span>
  `;
}
